import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Carousel } from 'antd';
import type { CarouselRef } from 'antd/lib/carousel';
import { DeleteOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';

export type CapturedItem = { original: string; extracted: string | null };

interface ImagePreviewCarouselModalProps {
  open: boolean;
  images: CapturedItem[];
  /** 開いた時に表示する初期インデックス */
  initialIndex: number;
  onClose: () => void;
  /** 指定すると削除ボタンを表示し、削除後の配列を返す */
  onImagesChange?: (images: CapturedItem[]) => void;
  zIndex?: number;
}

/**
 * 撮影／アップロード画像の拡大プレビュー（カルーセル）。
 * 連続撮影の確認画面・撮影中のサムネイルプレビュー・画像アップロードフローで共通利用する。
 */
const ImagePreviewCarouselModal: React.FC<ImagePreviewCarouselModalProps> = ({
  open,
  images,
  initialIndex,
  onClose,
  onImagesChange,
  zIndex = 1030,
}) => {
  const carouselRef = useRef<CarouselRef>(null);
  const [index, setIndex] = useState<number>(initialIndex);

  // 開くたびに初期インデックスへ
  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  const handleDelete = () => {
    if (!onImagesChange) return;
    const next = images.filter((_, i) => i !== index);
    onImagesChange(next);
    if (next.length === 0) {
      onClose();
      return;
    }
    const newIndex = Math.min(index, next.length - 1);
    setIndex(newIndex);
    setTimeout(() => carouselRef.current?.goTo(newIndex, true), 0);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width="92vw"
      zIndex={zIndex}
      styles={{ body: { padding: 0, backgroundColor: '#000' } }}
      destroyOnClose
    >
      {images.length > 0 && (
        <div style={{ position: 'relative' }}>
          {/* 枚数インジケータ */}
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              padding: '2px 10px',
              borderRadius: 12,
              fontSize: 13,
            }}
          >
            {Math.min(index + 1, images.length)} / {images.length}
          </div>

          {/* 削除ボタン */}
          {onImagesChange && (
            <Button
              type="primary"
              danger
              shape="circle"
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
            />
          )}

          <Carousel
            ref={carouselRef}
            initialSlide={Math.min(initialIndex, Math.max(images.length - 1, 0))}
            afterChange={(current) => setIndex(current)}
            draggable
            dots
          >
            {images.map((item, idx) => (
              <div key={idx}>
                <div
                  style={{
                    height: '75vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                  }}
                >
                  <img
                    src={item.extracted || item.original}
                    alt={`画像 ${idx + 1}`}
                    style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
                  />
                </div>
              </div>
            ))}
          </Carousel>

          {/* 前後ナビゲーション */}
          {images.length > 1 && (
            <>
              <Button
                shape="circle"
                icon={<LeftOutlined />}
                onClick={() => carouselRef.current?.prev()}
                style={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', zIndex: 2 }}
              />
              <Button
                shape="circle"
                icon={<RightOutlined />}
                onClick={() => carouselRef.current?.next()}
                style={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', zIndex: 2 }}
              />
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ImagePreviewCarouselModal;
